<template>
  <div class="experience-slider-container">
    <div class="exp-label">
      <span class="heading">Experience Level</span>
      <span class="exp-level">{{
        ratingLabels[expRating] || "Entry Level"
      }}</span>
    </div>
    <div class="slider-wrapper">
      <div class="tick-labels">
        <span
          v-for="(label, i) in ratingLabels"
          :key="label"
          :class="{ active: expRating === i }"
          >{{ label }}</span
        >
      </div>
      <div class="slider-markers" aria-hidden="true">
        <span
          v-for="marker in sliderMarkers"
          :key="marker.index"
          class="slider-marker"
          :class="{
            active: expRating >= marker.index,
            current: expRating === marker.index,
          }"
          :style="{ left: `${marker.left}%` }"
        />
      </div>
      <input
        type="range"
        class="experience-slider"
        :min="0"
        :max="ratingLabels.length - 1"
        :value="expRating"
        :disabled="!editMode"
        :style="sliderBackground"
        @input="handleSliderChange"
        @change="handleSliderChange"
      />
    </div>
    <div class="experience-desc" v-if="editMode">
      <span class="heading">{{ ratingLabels[expRating] }}</span>
      <p class="description">
        {{
          ratingDescription[ratingLabels[expRating].toLowerCase()] ||
          ratingDescription["apprentice"]
        }}
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: "loading-spinner",
  emits: ["on-change", "update:rating"],
  props: {
    rating: {
      type: Number,
    },
    isEditing: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      expRating: Number(this.rating ?? 0),
      editMode: this.isEditing ?? true,
      ratingLabels: ["Apprentice", "Skilled", "Journeyman", "Master"],
      ratingDescription: {
        apprentice:
          "Job seeker has limited experience and some formal training, but less than 2 years of work experience with this trade",
        skilled:
          "Job seeker has knowledge, training, skills, and a minimum 2 years of work experience with this trade",
        journeyman:
          "Job seeker can work independently with minimal supervision and has a minimum 4 years of work experience with this trade",
        master:
          "Job seeker has highest level of training and skills, can supervise workers, and has a minimum 6 years of work experience with this trade",
      },
    }
  },
  methods: {
    getSliderPercent() {
      const max = this.ratingLabels.length - 1
      if (max <= 0) {
        return 0
      }
      return (Number(this.expRating) / max) * 100
    },
    handleSliderChange(event) {
      const nextValue = Number(event.target.value)
      this.expRating = nextValue
      this.$emit("on-change", nextValue)
      this.$emit("update:rating", nextValue)
    },
  },
  computed: {
    sliderMarkers() {
      const max = this.ratingLabels.length - 1
      return this.ratingLabels
        .map((label, index) => ({
          label,
          index,
          left: max > 0 ? (index / max) * 100 : 0,
        }))
        .filter((marker) => marker.index > 0 && marker.index < max)
    },
    sliderBackground() {
      const percent = this.getSliderPercent()
      return {
        background: `linear-gradient(to right, #0095e2 0%, #0095e2 ${percent}%, #d5d9e2 ${percent}%, #d5d9e2 100%)`,
      }
    },
  },
  watch: {
    isEditing(newVal) {
      this.editMode = newVal
    },
    rating(newVal) {
      this.expRating = Number(newVal ?? 0)
    },
  },
}
</script>

<style lang="scss" scoped>
.experience-slider-container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  flex-direction: column;
  gap: 16px;

  span {
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    color: #525e77;
  }

  div.rating {
    display: flex;
    width: 42px;
    height: 32px;
    padding: 5px 16px 8px 16px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
    border-radius: 8px;
    border: 2px solid var(--Gray-500, #677690);
    background: var(--White, #fff);
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    color: var(--Gray-900, #343946);
    text-align: center;
    margin-left: 30px;
  }

  .slider-wrapper {
    position: relative;
    width: 100%;

    .slider-markers {
      position: absolute;
      inset: 0;
      pointer-events: none;
      z-index: 3;

      .slider-marker {
        position: absolute;
        top: 40%;
        width: 8px;
        height: 8px;
        transform: translate(-50%, -50%);
        border-radius: 50%;
        background: #7f8aa3;
        box-shadow: 0 0 0 2px #d5d9e2;

        &.active {
          background: #0073af;
          box-shadow: none;
        }

        &.current {
          background: unset;
        }
      }
    }

    .tick-labels {
      display: none;
      margin-bottom: 4px;

      span {
        font-size: 12px;
        color: var(--Gray-500, #677690);
        text-align: center;
        flex: 1;

        &.active {
          color: #1cd47e;
          font-weight: 600;
        }
      }
    }

    .experience-slider {
      width: 100%;
      height: 16px;
      cursor: pointer;
      appearance: none;
      -webkit-appearance: none;
      border-radius: 999px;
      background: linear-gradient(
        to right,
        #0095e2 0%,
        #0095e2 0%,
        #d5d9e2 0%,
        #d5d9e2 100%
      );
      position: relative;
      z-index: 2;

      &::-webkit-slider-runnable-track {
        height: 16px;
        border-radius: 999px;
        background: transparent;
      }

      &::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 20px;
        height: 20px;
        margin-top: -2px;
        border-radius: 50%;
        border: 2px solid #ffffff;
        background: #ffffff;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
      }

      &::-moz-range-track {
        height: 16px;
        border-radius: 999px;
        background: transparent;
      }

      &::-moz-range-thumb {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 2px solid #ffffff;
        background: #ffffff;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
      }

      &:disabled {
        cursor: default;
        opacity: 0.7;
      }
    }
  }
}

.exp-label {
  display: flex;
  width: 100%;
  justify-content: space-between;
  .heading {
    color: var(--Gray-800, #3a4252);
    font-family: Inter;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    text-transform: unset;
  }
  .exp-level {
    padding: 4px 8px;
    align-items: center;
    border-radius: 8px;
    background: var(--Gray-100, #eceef2);
    color: var(--Gray-800, #3a4252);
    font-family: Inter;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
  }
}

.experience-desc {
  display: flex;
  min-height: 90px;
  padding: 16px 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
  border-radius: 8px;
  background: var(--Gray-50, #f6f7f9);

  .heading {
    color: var(--Gray-800, #3a4252);
    text-align: center;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    text-transform: unset;
  }

  .description {
    color: var(--Gray-800, #3a4252);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
  }
}

@media (max-width: 768px) {
  .exp-label {
    flex-direction: column;
    gap: 12px;
  }
}
</style>
